import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { parseCoils } from '../room-overview/utils';
import type { MRIMachinePopulated } from '../room-overview/types';
import { labelSarRows } from '../../models/MRIMachineModels';
import type { SpatialGradientDTO } from '../../models/MRIMachineModels';

interface ScannerFormProps {
    template: MRIMachinePopulated;
    isSaving: boolean;
    onAdd: (name: string, coils: string) => void;
}

export default function ScannerForm({
    template,
    isSaving,
    onAdd,
}: ScannerFormProps) {
    const { t } = useTranslation();
    const coilInputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState('');
    const [coils, setCoils] = useState(template.TRCoils || '');
    const [coilInputValue, setCoilInputValue] = useState('');

    const currentCoils = parseCoils(coils);

    const addCoil = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (currentCoils.some((c) => c.toLowerCase() === trimmed.toLowerCase()))
            return;
        setCoils([...currentCoils, trimmed].join(', '));
    };

    const removeCoil = (index: number) => {
        const updated = [...currentCoils];
        updated.splice(index, 1);
        setCoils(updated.join(', '));
    };

    const handleSubmit = () => {
        if (!name.trim() || currentCoils.length === 0) return;
        onAdd(name.trim(), coils);
        setName('');
        setCoils('');
        setCoilInputValue('');
    };

    let rowIndex = 0;

    return (
        <>
            <div className="overflow-hidden rounded-lg border border-subtle bg-card">
                {/* Inputs */}
                <div className="grid gap-5 p-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="scanner-name">
                            {t('onboarding.scannerForm.roomName')}{' '}
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="scanner-name"
                            placeholder={t(
                                'onboarding.scannerForm.roomNamePlaceholder'
                            )}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="scanner-coils">
                            {t('onboarding.scannerForm.coils')}{' '}
                            <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                id="scanner-coils"
                                ref={coilInputRef}
                                placeholder={t(
                                    'onboarding.scannerForm.coilsPlaceholder'
                                )}
                                value={coilInputValue}
                                onChange={(e) =>
                                    setCoilInputValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        addCoil(coilInputValue);
                                        setCoilInputValue('');
                                    }
                                }}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 shrink-0"
                                disabled={!coilInputValue.trim()}
                                onClick={() => {
                                    addCoil(coilInputValue);
                                    setCoilInputValue('');
                                    coilInputRef.current?.focus();
                                }}
                                aria-label={t('onboarding.scannerForm.addCoil')}
                            >
                                <CornerDownLeft className="h-4 w-4" />
                            </Button>
                        </div>
                        {currentCoils.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {currentCoils.map((coil, i) => (
                                    <Badge
                                        key={i}
                                        variant="outline"
                                        className="gap-1 border-transparent bg-gray-200 pr-1 text-xs text-gray-800 hover:bg-gray-200"
                                    >
                                        {coil}
                                        <button
                                            type="button"
                                            onClick={() => removeCoil(i)}
                                            className="ml-0.5 rounded-full p-0.5 hover:bg-gray-500/30"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="divide-y divide-subtle">
                    <SpecRow
                        label="Model"
                        value={template.Model}
                        alternate={rowIndex++ % 2 === 1}
                    />
                    {template.Manufacturer && (
                        <SpecRow
                            label="Manufacturer"
                            value={template.Manufacturer}
                            alternate={rowIndex++ % 2 === 1}
                        />
                    )}
                    <SpecRow
                        label="Static Magnetic Field"
                        value={`${template.FieldStrength} Tesla`}
                        alternate={rowIndex++ % 2 === 1}
                    />
                    {template.SpatialGradient?.map(
                        (sg: SpatialGradientDTO, i: number) => {
                            const isNumeric = /^\d+(\.\d+)?$/.test(
                                String(sg.diameter).trim()
                            );
                            const label = isNumeric
                                ? `Spatial Gradient (${sg.diameter} cm DSV)`
                                : `Spatial Gradient (${sg.diameter})`;
                            return (
                                <SpecRow
                                    key={`sg-${i}`}
                                    label={label}
                                    value={`${sg.value} T/m (${Math.round(sg.value * 100)} Gauss/cm)`}
                                    alternate={rowIndex++ % 2 === 1}
                                />
                            );
                        }
                    )}
                    <SpecRow
                        label="Max Slew Rate / Axis"
                        value={`${template.SlewRatePerAxis} T/m/s`}
                        alternate={rowIndex++ % 2 === 1}
                    />
                    <SpecRow
                        label="Max Effective Slew Rate"
                        value={`${template.MaxSlewRate} T/m/s`}
                        alternate={rowIndex++ % 2 === 1}
                    />
                    {template.MaxSAR?.length > 0 && (
                        <SpecRow
                            label="Max SAR"
                            value={
                                <div className="space-y-0.5">
                                    {labelSarRows(template.MaxSAR).map(
                                        (sar, i) => (
                                            <div key={i}>
                                                {sar.label && `${sar.label} — `}
                                                Whole Body {sar.wholeBody} W/kg
                                                — Head {sar.head} W/kg
                                            </div>
                                        )
                                    )}
                                </div>
                            }
                            alternate={rowIndex++ % 2 === 1}
                        />
                    )}
                    <SpecRow
                        label="Max B1+rms"
                        value={
                            template.MaxB1rms
                                ? `${template.MaxB1rms}${!isNaN(Number(template.MaxB1rms)) ? ' µT' : ''}`
                                : 'N/A'
                        }
                        alternate={rowIndex++ % 2 === 1}
                    />
                    <SpecRow
                        label={t('onboarding.scannerForm.coils')}
                        value={
                            currentCoils.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {currentCoils.map((coil, i) => (
                                        <Badge
                                            key={i}
                                            variant="outline"
                                            className="border-transparent bg-gray-200 text-xs text-gray-800"
                                        >
                                            {coil}
                                        </Badge>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-muted-foreground">
                                    {t('onboarding.scannerForm.noCoilsAdded')}
                                </span>
                            )
                        }
                        alternate={rowIndex++ % 2 === 1}
                    />
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    onClick={handleSubmit}
                    disabled={
                        !name.trim() || currentCoils.length === 0 || isSaving
                    }
                >
                    {isSaving
                        ? t('onboarding.scannerForm.adding')
                        : t('onboarding.scannerForm.addScanner')}
                </Button>
            </div>
        </>
    );
}

interface SpecRowProps {
    label: string;
    value: React.ReactNode;
    alternate?: boolean;
}

function SpecRow({ label, value, alternate = false }: SpecRowProps) {
    return (
        <div
            className={cn(
                'grid grid-cols-2 gap-4 px-5 py-3',
                alternate ? 'bg-accent' : 'bg-transparent'
            )}
        >
            <div className="text-sm font-medium">{label}</div>
            <div className="text-sm tabular-nums">{value}</div>
        </div>
    );
}
